"use client";

import type React from "react";

import { motion } from "framer-motion";
import {
  CheckCircle,
  Hash,
  ImageIcon,
  Plus,
  Share,
  Type,
  Wand2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import dreamWorldProvider from "../../DreamWorldProvider";
import { LibraryItem } from "../../types/library";
import eventBus from "../../utils/eventBus";

interface PublishContentProps {
  item: LibraryItem;
  onClose: () => void;
  stopFullScreenClose?: boolean;
  effectId?: string;
}

const PublishContent: React.FC<PublishContentProps> = ({
  item,
  onClose,
  stopFullScreenClose,
  effectId,
}) => {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>(item.tags);
  const [newTag, setNewTag] = useState("");
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      if (stopFullScreenClose !== true) {
        eventBus.emit("fullscreen:exit");
      }
    };
  }, []);

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleGenerateAITags = async () => {
    if (!item.thumbnailUrl) {
      // Or handle this case more gracefully
      console.error("No image URL available to generate tags from.");
      return;
    }
    setIsGeneratingTags(true);
    try {
      const commaSeparatedTags = await dreamWorldProvider.generateTagsFromImage(
        item.thumbnailUrl
      );
      const aiGeneratedTags = commaSeparatedTags
        .split(",")
        .map((tag) => tag.trim());
      const newTags = aiGeneratedTags.filter((tag) => !tags.includes(tag));
      setTags([...tags, ...newTags]);
    } catch (error) {
      console.error("Error generating AI tags:", error);
      // Optionally, show an error message to the user
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handlePublish = async () => {
    if (item.isPublished || isPublishing || !title.trim()) return;
    setIsPublishing(true);
    try {
      await dreamWorldProvider.publishContent({
        id: item.id,
        title,
        description,
        tags,
        effectId,
      });
      setShowSuccess(true);
    } catch (error) {
      console.error("Error publishing content:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const isDisabled = !title.trim() || item.isPublished === true || isPublishing;
  const buttonText =
    item.isPublished === true
      ? "Already Published"
      : isPublishing
        ? "Publishing..."
        : "Publish";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{
          x: window.innerWidth <= 768 ? "100%" : 0,
          scale: window.innerWidth <= 768 ? 1 : 0.9,
          opacity: window.innerWidth <= 768 ? 1 : 0,
        }}
        animate={{
          x: 0,
          scale: 1,
          opacity: 1,
        }}
        exit={{
          x: window.innerWidth <= 768 ? "100%" : 0,
          scale: window.innerWidth <= 768 ? 1 : 0.9,
          opacity: window.innerWidth <= 768 ? 1 : 0,
        }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden
                   md:rounded-2xl md:max-w-4xl
                   max-md:rounded-none max-md:max-w-none max-md:h-full max-md:max-h-none max-md:fixed max-md:right-0 max-md:top-0 max-md:bottom-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header for Desktop */}
        <div className="hidden md:flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
              <Share size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Publish Content</h2>
              <p className="text-white/60 text-sm">
                Share your creation with the world
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} className="text-white/60" />
          </button>
        </div>

        {/* Header for Mobile */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-white/10">
          <button
            onClick={onClose}
            className="p-2 text-sm font-medium text-white/80 hover:bg-white/10 rounded-lg"
          >
            Cancel
          </button>
          <motion.button
            onClick={handlePublish}
            disabled={isDisabled}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-2"
            whileTap={{ scale: 0.95 }}
          >
            {isPublishing && (
              <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
            )}
            {buttonText}
          </motion.button>
        </div>

        <div className="flex flex-col lg:flex-row max-h-[calc(100vh-80px)] md:max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Content Preview */}
          <div className="lg:w-1/2 p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-white/10">
            <div className="space-y-4">
              <div className="aspect-square bg-white/5 rounded-xl overflow-hidden">
                {item.type === "Video" ? (
                  <video
                    src={
                      item.thumbnailUrl ||
                      "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                    }
                    className="w-full h-full object-cover"
                    controls
                    muted
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={
                      item.thumbnailUrl ||
                      "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                    }
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Type size={14} className="text-purple-400" />
                    <span className="text-sm font-medium text-white/90">
                      Original Prompt
                    </span>
                  </div>
                  <p className="text-sm text-white/70 leading-relaxed">
                    {item.title}
                  </p>
                </div>

                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
                    <span className="text-sm text-white/70">
                      Style: {item.tags[0]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ImageIcon size={14} className="text-white/50" />
                    <span className="text-xs text-white/50">{item.type}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:w-1/2 p-4 md:p-6 lg:overflow-y-auto">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your creation a title..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  disabled={item.isPublished === true}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your creation, inspiration, or process..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all resize-none"
                  disabled={item.isPublished === true}
                />
              </div>

              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-white/90">
                    Tags
                  </label>
                  <motion.button
                    onClick={handleGenerateAITags}
                    disabled={isGeneratingTags || item.isPublished === true}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isGeneratingTags ? (
                      <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Wand2 size={12} />
                    )}
                    Tag with AI
                  </motion.button>
                </div>

                {/* Tag Input */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Add a tag..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg p-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                    disabled={item.isPublished === true}
                  />
                  <motion.button
                    onClick={handleAddTag}
                    disabled={!newTag.trim() || item.isPublished === true}
                    className="p-2 bg-white/10 border border-white/10 rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Plus size={16} className="text-white/80" />
                  </motion.button>
                </div>

                {/* Tags Display */}
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-full text-sm text-purple-200"
                    >
                      <Hash size={12} />
                      <span>{tag}</span>
                      {!item.isPublished && (
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-white transition-colors"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Publish Button - Desktop only */}
              <div className="pt-4 border-t border-white/10 hidden md:block">
                <motion.button
                  onClick={handlePublish}
                  disabled={isDisabled}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base flex items-center justify-center gap-2"
                  whileHover={{ scale: title.trim() ? 1.02 : 1 }}
                  whileTap={{ scale: title.trim() ? 0.98 : 1 }}
                >
                  {isPublishing && (
                    <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  )}
                  {buttonText}
                </motion.button>
                <p className="text-xs text-white/50 text-center mt-2">
                  Your content will be shared publicly
                </p>
              </div>
            </div>
          </div>
        </div>

        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute inset-0 bg-black/80 p-2 flex items-center justify-center z-50"
          >
            <div className="bg-gray-900/95 p-8 rounded-2xl border border-white/10 text-center max-w-sm w-full">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Published!</h2>
              <p className="text-white/70 mb-6">
                Your content has been successfully published.
              </p>
              <motion.button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PublishContent;
